// ============================================
// Storm Report — Hurricane Inspector Dashboard
//
// Shows on the dev landing page. Reads from Firestore:
//   storm_reports/{sessionId} — aggregated report
//   storm_inspections/{directiveId} — per-room inspections
//   _ops/storm_heartbeat — live scheduler status
//   stan_directives (pending count) — forecast
//
// Mobile-first. Phone-screen friendly.
// Clicking a room opens a detail drawer with camera results.
// ============================================

import { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs, doc, getDoc, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { T, F } from '../quality/tokens';
import { C_STORM_REPORTS, C_STAN_DIRECTIVES, C_OPS, OPS_DOC_STORM_HEARTBEAT, C_STORM_INSPECTIONS } from '../../constants/collections';

interface CamFinding {
  type: string;
  detail: string;
  severity: 'high' | 'medium' | 'low';
}

interface CamResult {
  viewport: string;
  route: string;
  verdict: 'PASS' | 'FLAG' | 'FAIL' | 'ERROR';
  summary?: string;
  reason?: string;
  findings?: CamFinding[];
  criticalContent?: Array<{ item: string; found: boolean; detail: string }>;
  bodyPreview?: string;
  elementCount?: number;
}

interface InspectionDetail {
  directiveId: string;
  task: string;
  priority: string;
  verdict: 'PASS' | 'FLAG' | 'FAIL' | 'INCOMPLETE';
  flags: string[];
  duration: number;
  wallyVerdict?: string;
  wallyNotes?: string;
  nickVerdict?: string;
  nickNotes?: string;
  cameraVerdict?: string;
  cameraNotes?: string;
  camResults?: CamResult[] | null;
  gateResult?: string;
  checks?: Record<string, boolean>;
  criteriaResults?: Array<{
    criterionId: string;
    description: string;
    workerClaim: string;
    verified: boolean;
  }>;
}

interface Inspection {
  directiveId: string;
  task: string;
  priority: string;
  verdict: 'PASS' | 'FLAG' | 'FAIL';
  flags: string[];
  duration: number;
}

interface StormReportData {
  verdict: 'ALL_CLEAR' | 'FLAGS_FOUND';
  roomsBuilt: number;
  passed: number;
  flagged: number;
  failed: number;
  wavesRun: number;
  shifts: number;
  uptimeMinutes: number;
  manDays: number;
  inspections: Inspection[];
  generatedAt: { seconds: number } | null;
}

interface Heartbeat {
  activeWorkers: number;
  pendingRooms: number;
  currentWave: number;
  maxSlots: number;
  effectiveSlots: number;
  consecutiveFailures: number;
  lastBeat: { seconds: number } | null;
}

export function StormReport() {
  const [report, setReport] = useState<StormReportData | null>(null);
  const [heartbeat, setHeartbeat] = useState<Heartbeat | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [selectedDirective, setSelectedDirective] = useState<string | null>(null);
  const [detail, setDetail] = useState<InspectionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  async function fetchData() {
    setLoading(true);
    try {
      // Latest storm report
      const reportsQ = query(
        collection(db, C_STORM_REPORTS),
        orderBy('generatedAt', 'desc'),
        limit(1),
      );
      const reportsSnap = await getDocs(reportsQ);
      if (!reportsSnap.empty) {
        setReport(reportsSnap.docs[0].data() as StormReportData);
      }

      // Heartbeat
      const hbDoc = await getDoc(doc(db, C_OPS, OPS_DOC_STORM_HEARTBEAT));
      if (hbDoc.exists()) {
        setHeartbeat(hbDoc.data() as Heartbeat);
      }

      // Pending directives count (forecast)
      const pendingQ = query(
        collection(db, C_STAN_DIRECTIVES),
        where('status', '==', 'pending'),
      );
      const pendingSnap = await getDocs(pendingQ);
      setPendingCount(pendingSnap.size);

      setLastRefresh(new Date());
    } catch (e) {
      console.error('Storm report fetch failed:', e);
    }
    setLoading(false);
  }

  async function fetchDetail(directiveId: string) {
    if (selectedDirective === directiveId) {
      setSelectedDirective(null);
      setDetail(null);
      return;
    }
    setSelectedDirective(directiveId);
    setDetailLoading(true);
    try {
      const snap = await getDoc(doc(db, C_STORM_INSPECTIONS, directiveId));
      if (snap.exists()) {
        setDetail(snap.data() as InspectionDetail);
      } else {
        setDetail(null);
      }
    } catch (e) {
      console.error('Inspection detail fetch failed:', e);
      setDetail(null);
    }
    setDetailLoading(false);
  }

  useEffect(() => { fetchData(); }, []);

  const verdictIcon = (v: string) =>
    v === 'PASS' ? '\u2705' : v === 'FLAG' ? '\uD83D\uDFE1' : v === 'FAIL' ? '\uD83D\uDD34' : '\u2B1C';

  // camVerdictColor reserved for future use

  const isOnline = heartbeat?.lastBeat
    ? (Date.now() / 1000 - heartbeat.lastBeat.seconds) < 120
    : false;

  return (
    <div style={{
      width: '100%', maxWidth: 420, marginTop: 24,
      background: T.bgCard, border: `1px solid ${T.border}`,
      borderRadius: T.radius.lg, boxShadow: T.shadowMd,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${T.border}`,
        background: '#18181B',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>{isOnline ? '\uD83C\uDFE8' : '\uD83D\uDCA4'}</span>
          <div>
            <div style={{
              fontFamily: F.mono, fontSize: T.fontSize.sm, fontWeight: 700,
              color: '#FFFFFF', letterSpacing: 0.5,
            }}>
              STORM REPORT
            </div>
            <div style={{
              fontFamily: F.mono, fontSize: 11, color: '#71717A', marginTop: 1,
            }}>
              {isOnline ? 'Scheduler online' : 'Scheduler idle'}
              {heartbeat ? ` \u00B7 Wave ${heartbeat.currentWave}` : ''}
            </div>
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          style={{
            background: 'none', border: `1px solid #3F3F46`, borderRadius: T.radius.md,
            color: '#A1A1AA', fontFamily: F.mono, fontSize: 11, padding: '4px 10px',
            cursor: 'pointer', fontWeight: 600,
          }}
        >
          {loading ? '...' : 'REFRESH'}
        </button>
      </div>

      {/* Live Status Bar */}
      {heartbeat && isOnline && (
        <div style={{
          padding: '10px 20px', display: 'flex', gap: 16,
          borderBottom: `1px solid ${T.border}`,
          background: '#FAFAFA',
        }}>
          <Stat label="Active" value={`${heartbeat.activeWorkers}/${heartbeat.maxSlots}`} />
          <Stat label="Pending" value={String(heartbeat.pendingRooms)} />
          <Stat label="Queue" value={String(pendingCount)} />
          {heartbeat.consecutiveFailures > 0 && (
            <Stat label="Errors" value={String(heartbeat.consecutiveFailures)} alert />
          )}
        </div>
      )}

      {/* Latest Report */}
      {report ? (
        <div style={{ padding: '16px 20px' }}>
          {/* Verdict Banner */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 14,
          }}>
            <div style={{
              fontFamily: F.mono, fontSize: T.fontSize.sm, fontWeight: 700,
              color: report.verdict === 'ALL_CLEAR' ? '#16A34A' : '#DC2626',
              letterSpacing: 0.5,
            }}>
              {report.verdict === 'ALL_CLEAR' ? '\u2705 ALL CLEAR' : '\uD83D\uDEA8 FLAGS FOUND'}
            </div>
            <div style={{
              fontFamily: F.mono, fontSize: 11, color: T.textMuted,
            }}>
              {report.generatedAt
                ? new Date(report.generatedAt.seconds * 1000).toLocaleString()
                : ''}
            </div>
          </div>

          {/* Summary Row */}
          <div style={{
            display: 'flex', gap: 12, marginBottom: 16,
            padding: '10px 14px', background: '#F4F4F5', borderRadius: T.radius.md,
          }}>
            <Stat label="Built" value={String(report.roomsBuilt)} />
            <Stat label="Pass" value={String(report.passed)} />
            <Stat label="Flag" value={String(report.flagged)} alert={report.flagged > 0} />
            <Stat label="Fail" value={String(report.failed)} alert={report.failed > 0} />
            <Stat label="Man-Days" value={String(report.manDays || 0)} />
          </div>

          {/* Room List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {(report.inspections || []).map((insp, i) => (
              <div key={insp.directiveId || i}>
                <div
                  onClick={() => insp.directiveId && fetchDetail(insp.directiveId)}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    padding: '8px 0',
                    borderBottom: (selectedDirective !== insp.directiveId && i < report.inspections.length - 1)
                      ? `1px solid ${T.border}` : 'none',
                    cursor: insp.directiveId ? 'pointer' : 'default',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => { if (insp.directiveId) e.currentTarget.style.background = T.bgSoft; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontSize: 14, marginTop: 1, flexShrink: 0 }}>
                    {verdictIcon(insp.verdict)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 500,
                      color: T.text, lineHeight: 1.3,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical' as const,
                    }}>
                      {insp.task}
                    </div>
                    {insp.flags.length > 0 && (
                      <div style={{
                        fontFamily: F.mono, fontSize: 11, color: '#DC2626',
                        marginTop: 3,
                      }}>
                        {insp.flags[0]}
                      </div>
                    )}
                  </div>
                  <div style={{
                    fontFamily: F.mono, fontSize: 11, color: T.textMuted,
                    flexShrink: 0, marginTop: 2,
                  }}>
                    {insp.duration}s
                  </div>
                </div>

                {/* Detail Drawer */}
                {selectedDirective === insp.directiveId && (
                  <DirectiveDetail detail={detail} loading={detailLoading} />
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{
          padding: '32px 20px', textAlign: 'center',
          fontFamily: F.body, fontSize: T.fontSize.sm, color: T.textMuted,
        }}>
          {loading ? 'Loading...' : 'No storm reports yet. Run the scheduler to generate.'}
        </div>
      )}

      {/* Forecast */}
      {pendingCount > 0 && (
        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${T.border}`,
          background: '#FFFBEB',
        }}>
          <div style={{
            fontFamily: F.mono, fontSize: 11, fontWeight: 600,
            color: '#92400E', letterSpacing: 0.5,
          }}>
            FORECAST: {pendingCount} directive{pendingCount !== 1 ? 's' : ''} in queue
          </div>
        </div>
      )}

      {/* Timestamp */}
      <div style={{
        padding: '8px 20px', textAlign: 'right',
        fontFamily: F.mono, fontSize: 10, color: T.textGhost,
        borderTop: `1px solid ${T.border}`,
      }}>
        Last refresh: {lastRefresh.toLocaleTimeString()}
      </div>
    </div>
  );
}

// ── Directive Detail Drawer ──
function DirectiveDetail({ detail, loading }: { detail: InspectionDetail | null; loading: boolean }) {
  const camVerdictColor = (v: string) =>
    v === 'PASS' ? T.success : v === 'FLAG' ? T.warning : v === 'FAIL' ? T.danger : T.textMuted;

  if (loading) {
    return (
      <div style={{
        padding: '12px 16px', background: T.bgSoft, borderRadius: T.radius.md,
        marginBottom: 8, fontFamily: F.mono, fontSize: T.fontSize.sm, color: T.textMuted,
      }}>
        Loading inspection...
      </div>
    );
  }

  if (!detail) {
    return (
      <div style={{
        padding: '12px 16px', background: T.bgSoft, borderRadius: T.radius.md,
        marginBottom: 8, fontFamily: F.mono, fontSize: T.fontSize.sm, color: T.textMuted,
      }}>
        No inspection data found
      </div>
    );
  }

  return (
    <div style={{
      padding: '12px 16px', background: T.bgSoft, borderRadius: T.radius.md,
      marginBottom: 8, borderLeft: `3px solid ${camVerdictColor(detail.verdict)}`,
    }}>
      {/* Gate Result */}
      {detail.gateResult && (
        <div style={{
          fontFamily: F.mono, fontSize: 11, fontWeight: 700,
          color: detail.gateResult === 'SHIP' ? T.success : detail.gateResult === 'HARD_FAIL' ? T.danger : T.warning,
          letterSpacing: 0.5, marginBottom: 8,
        }}>
          GATE: {detail.gateResult}
        </div>
      )}

      {/* Three Validators */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <ValidatorBadge name="Wally" verdict={detail.wallyVerdict} notes={detail.wallyNotes} />
        <ValidatorBadge name="Nick P" verdict={detail.nickVerdict} notes={detail.nickNotes} />
        <ValidatorBadge name="CAM" verdict={detail.cameraVerdict} notes={detail.cameraNotes} />
      </div>

      {/* Camera Results */}
      {detail.camResults && detail.camResults.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{
            fontFamily: F.mono, fontSize: 11, fontWeight: 600,
            color: T.textSoft, letterSpacing: 0.5, marginBottom: 6,
          }}>
            CAMERA RESULTS
          </div>
          {detail.camResults.map((cam, i) => (
            <CamResultRow key={`${cam.viewport}-${cam.route}-${i}`} cam={cam} />
          ))}
        </div>
      )}

      {/* Criteria Results */}
      {detail.criteriaResults && detail.criteriaResults.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{
            fontFamily: F.mono, fontSize: 11, fontWeight: 600,
            color: T.textSoft, letterSpacing: 0.5, marginBottom: 4,
          }}>
            CRITERIA ({detail.criteriaResults.filter(c => c.verified).length}/{detail.criteriaResults.length})
          </div>
          {detail.criteriaResults.map((c) => (
            <div key={c.criterionId} style={{
              fontFamily: F.mono, fontSize: 11, color: c.verified ? T.success : T.danger,
              padding: '2px 0',
            }}>
              {c.verified ? '\u2705' : '\u274C'} [{c.criterionId}] {c.description.slice(0, 60)}
            </div>
          ))}
        </div>
      )}

      {/* Flags */}
      {detail.flags && detail.flags.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{
            fontFamily: F.mono, fontSize: 11, fontWeight: 600,
            color: T.textSoft, letterSpacing: 0.5, marginBottom: 4,
          }}>
            FLAGS ({detail.flags.length})
          </div>
          {detail.flags.slice(0, 5).map((f, i) => (
            <div key={i} style={{
              fontFamily: F.mono, fontSize: 11, color: T.danger,
              padding: '2px 0', lineHeight: 1.3,
            }}>
              {f.slice(0, 120)}
            </div>
          ))}
          {detail.flags.length > 5 && (
            <div style={{ fontFamily: F.mono, fontSize: 11, color: T.textMuted }}>
              +{detail.flags.length - 5} more
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Validator Badge ──
function ValidatorBadge({ name, verdict, notes }: { name: string; verdict?: string; notes?: string }) {
  const color = verdict === 'PASS' ? T.success
    : verdict === 'FAIL' ? T.danger
    : verdict === 'FLAG' ? T.warning
    : verdict === 'SKIPPED' ? T.textMuted
    : T.textGhost;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 2,
      padding: '4px 8px', background: T.bgCard,
      borderRadius: T.radius.sm, border: `1px solid ${T.borderSoft}`,
      minWidth: 80,
    }} title={notes || ''}>
      <div style={{
        fontFamily: F.mono, fontSize: 10, fontWeight: 600,
        color: T.textMuted, letterSpacing: 0.5,
      }}>
        {name}
      </div>
      <div style={{
        fontFamily: F.mono, fontSize: T.fontSize.sm, fontWeight: 700,
        color,
      }}>
        {verdict || 'N/A'}
      </div>
    </div>
  );
}

// ── Camera Result Row ──
function CamResultRow({ cam }: { cam: CamResult }) {
  const color = cam.verdict === 'PASS' ? T.success
    : cam.verdict === 'FLAG' ? T.warning
    : cam.verdict === 'FAIL' ? T.danger
    : T.textMuted;

  const severityColor = (s: string) =>
    s === 'high' ? T.danger : s === 'medium' ? T.warning : T.textMuted;

  return (
    <div style={{
      padding: '6px 8px', background: T.bgCard,
      borderRadius: T.radius.sm, marginBottom: 4,
      border: `1px solid ${T.borderSoft}`,
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
        <span style={{
          fontFamily: F.mono, fontSize: 10, fontWeight: 700,
          color, textTransform: 'uppercase',
        }}>
          {cam.verdict}
        </span>
        <span style={{
          fontFamily: F.mono, fontSize: 10, color: T.textMuted,
        }}>
          {cam.viewport} {cam.route}
        </span>
      </div>

      {/* Summary */}
      {cam.summary && (
        <div style={{
          fontFamily: F.body, fontSize: T.fontSize.sm, color: T.textSoft,
          lineHeight: 1.3, marginBottom: 4,
        }}>
          {cam.summary}
        </div>
      )}

      {/* Findings */}
      {cam.findings && cam.findings.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {cam.findings.map((f, i) => (
            <div key={i} style={{
              display: 'flex', gap: 4, alignItems: 'flex-start',
              padding: '2px 0',
            }}>
              <span style={{
                fontFamily: F.mono, fontSize: 10, fontWeight: 600,
                color: severityColor(f.severity),
                flexShrink: 0, minWidth: 14, textAlign: 'center',
              }}>
                {f.severity === 'high' ? '!' : f.severity === 'medium' ? '~' : '-'}
              </span>
              <span style={{
                fontFamily: F.mono, fontSize: 11, color: T.text,
                lineHeight: 1.3,
              }}>
                <span style={{ color: severityColor(f.severity), fontWeight: 600 }}>[{f.type}]</span>{' '}
                {f.detail.slice(0, 100)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Error reason */}
      {cam.reason && (
        <div style={{
          fontFamily: F.mono, fontSize: 11, color: T.danger,
          marginTop: 2,
        }}>
          {cam.reason}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div style={{ textAlign: 'center', flex: 1 }}>
      <div style={{
        fontFamily: F.mono, fontSize: T.fontSize.lg, fontWeight: 700,
        color: alert ? '#DC2626' : T.text,
      }}>
        {value}
      </div>
      <div style={{
        fontFamily: F.mono, fontSize: 10, color: T.textMuted,
        textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        {label}
      </div>
    </div>
  );
}
